import { HISTORICAL_AUDIT_ACTIONS, type AuditAction } from "@shared/audit/actions";

/**
 * Words for the audit actions a person reads most often as a story, and for the rows the runner
 * writes on its own (walk 2026-10-02, D13: `tenant.provision` read as a raw key).
 *
 * Round 6 T-04 (owner 2026-10-01: "Do not use the word Customer, as all we do is creating tenant
 * and instances within them"; 2026-10-02: "Rename. No need to use Customer"): a tenant's set-up
 * writes one `tenant.create` and one `instance.create` per instance in its own transaction
 * (`TenantService.create`), all under one correlation id. Platform 0027 folded the customers into
 * their tenants, so `customer.create` and `customer.update` are history: rows written before it
 * keep their stored action, and the grids label them "Tenant set up" and "Tenant updated" with
 * the entity "tenant", so the word Customer does not appear in the trail. The machine name stays
 * in the event detail and the CSV, which is where an auditor searches for it; every trail's
 * Action filter offers the same words, a historical one with "(history)"
 * (`auditActionOptionText`), and the entity "tenant" (`AuditFilters`).
 *
 * Round 6 T-29 (owner 2026-10-02: "go with option 2, dbt job"): a saved dbt selection is a dbt
 * job, audited as `dbt_job.*` on entity `dbt_job`. Rows written before keep `transformation.*`
 * on entity `transformation`; the grids read them in today's words, on entity `dbt_job`.
 *
 * Round 7 T-36 (owner: "Go ahead and do"): EVERY action has words, in the grid and the filter of
 * every trail. The table is typed by the closed set, so a new action in `shared/audit/actions.ts`
 * does not compile without its words, and `auditActionLabels.complete.test.ts` also checks the
 * route registry and the runner's own SQL. `audit.export` reads "Audit records exported": a
 * person's CSV download writes it as well as the runner's archive. An action outside the set (a
 * row newer than this build) shows its machine name alone, verbatim.
 */
export const AUDIT_ACTION_LABELS: Readonly<Record<AuditAction, string>> = {
  "auth.sign_in": "Signed in",
  "auth.sign_in_failed": "Sign-in failed",
  "auth.sign_out": "Signed out",
  "auth.token_rejected": "Sign-in token refused",
  "auth.denied": "Access denied",
  "auth.reauth": "Signed in again",
  "auth.mfa_device_remember": "Browser remembered after MFA",
  "auth.mfa_device_forget": "Remembered browser forgotten",
  "access.grant": "Access granted",
  "access.revoke": "Access removed",
  "access.reactivate": "Access restored",
  // Round 7: a pending person's invitation email, sent again.
  "access.invite_resend": "Invitation resent",
  "user.create": "User added",
  "user.activate": "User activated",
  "user.deactivate": "User deactivated",
  "user.rebind": "Sign-in provider changed",
  // #1639 C1: the runner closed a request for a person's email sign-in account.
  "account.create": "Email sign-in account requested",
  "group_mapping.create": "Directory group mapped",
  "group_mapping.remove": "Directory group mapping removed",
  "access.group_grant": "Access granted by a directory group",
  "access.group_end": "Directory group access ended",
  "access.group_override_set": "Group member's role overridden",
  "access.group_enforcement_exempt": "Break-glass access to a tenant",
  "tenant.group_enforcement_update": "Directory group requirement changed",
  "customer.create": "Tenant set up",
  "customer.update": "Tenant updated",
  "tenant.create": "Tenant created",
  "tenant.update": "Tenant updated",
  "tenant.connector_access_update": "Connectors offered changed",
  "tenant.connector_access_platform_update": "Connectors offered set by Skypoint",
  "tenant.notification_domains_update": "Allowed notification domains changed",
  // History (ADR 0011, 2026-10-08): the Explorer and its tenant switch are removed; stored rows
  // keep their words.
  "tenant.warehouse_catalog_update": "Warehouse catalog switched by Skypoint",
  // Walk 2026-10-02, D13: the runner's own rows read as words too (`runner/steps/`, `runner/audit/`).
  "tenant.provision": "Tenant database provisioned",
  // ADR 0010: the tenant's own Azure resources (a Retry, and the provisioner's own rows).
  "tenant.resources_retry": "Tenant Azure resources retried",
  "tenant.resources_apply": "Tenant Azure resources applied again",
  "tenant.delete": "Tenant deleted",
  "canary.configure": "Canary configured",
  "canary.remove": "Canary removed",
  "tenant.resources_provision": "Tenant Azure resources provisioned",
  "tenant.resources_grant_revoked": "Tenant Azure grant revoked",
  "tenant.resources_orphan_found": "Orphaned tenant Azure resource found",
  "tenant.resources_orphan_deleted": "Orphaned tenant Azure identity deleted",
  "tenant.resources_grant_found": "Unexpected tenant Azure grant found",
  "tenant.resources_generation_raised": "Tenant Azure resource name in use, generation raised",
  "instance.create": "Instance created",
  "instance.update": "Instance updated",
  "instance.provision": "Instance provisioned",
  "instance.disable": "Instance disabled",
  "instance.enable": "Instance enabled",
  "instance.provisioning_retry": "Instance provisioning retried",
  "instance.mark_storage_removed": "Instance storage marked removed",
  "instance.convert": "Instance moved into its tenant database",
  "binding.read": "Destination settings read",
  "binding.update": "Destination settings updated",
  "binding.probe": "dbt repository checked",
  "binding.test": "Destination tested",
  "connector.seed": "Connector catalog loaded",
  "connector.update": "Connector updated",
  "connector.version.add": "Connector version added",
  "connector.availability_update": "Connector availability changed",
  "connector.remove_catalog_only": "Unused catalog-only connector removed",
  "dataflow.create": "Connection created",
  "dataflow.update": "Connection updated",
  "dataflow.delete": "Connection deleted",
  "dataflow.read": "Connection settings read",
  "dataflow.validate": "Connection tested",
  "dataflow.revalidate": "Connection tested again",
  "dataflow.state_change": "Connection state changed",
  "dataflow.disable": "Connection turned off",
  "dataflow.enable": "Connection turned on",
  "dataflow.schema_policy.update": "Schema change policy updated",
  "dataflow.schema_change.decide": "Schema change decided",
  "dataflow.entities.update": "Connection tables updated",
  "hub.browse": "Source tables listed",
  "secret.write": "Secret saved",
  "secret.read": "Secret read",
  "secret.delete": "Secret deleted",
  "credential.expiry.update": "Credential expiry date set",
  "schedule.create": "Schedule created",
  "schedule.update": "Schedule updated",
  "schedule.delete": "Schedule deleted",
  "schedule.toggle": "Schedule turned on or off",
  "schedule.pause": "Schedule paused",
  "schedule.resume": "Schedule resumed",
  "schedule.skip": "Scheduled run skipped",
  "run.start": "Run started",
  "run.fire": "Scheduled run started",
  "run.retry": "Run retried",
  "run.retry_tables": "Failed tables retried",
  "run.cancel": "Run cancelled",
  "run.force_reset": "Stuck run reset",
  "run.technical_details_view": "Technical details viewed",
  "run.finalize": "Run finished",
  "step.transition": "Run step moved on",
  "dbt_job.create": "dbt job created",
  "dbt_job.update": "dbt job updated",
  "dbt_job.delete": "dbt job deleted",
  "dbt_job.sql.read": "dbt node SQL read",
  // Round 7: the runner's maintenance pass (`runner/maintenance/`).
  "run.retention": "Old runs removed",
  "dataflow.refresh_type.derive": "Refresh type set from connector",
  // Round 7 (T-31): the Explorer's Refresh. History since ADR 0011 (2026-10-08).
  "explorer.refresh": "Explorer refresh requested",
  // Round 6 T-29: history, written before the rename.
  "transformation.create": "dbt job created",
  "transformation.update": "dbt job updated",
  "transformation.delete": "dbt job deleted",
  "transformation.sql.read": "dbt node SQL read",
  "settings.update": "Settings updated",
  "migration.apply": "Database migration applied",
  "notification.rules.update": "Notification rules updated",
  "notification.test": "Test notification sent",
  "notification.first_line.update": "First-line contacts updated",
  "notification.watch.update": "Watch changed",
  "notification.security.update": "Security alert subscription changed",
  "notification.release": "Parked emails released",
  "user.preferences.update": "Preferences updated",
  "user.last_instance.update": "Instance switched",
  "user.display_name_refresh": "Name updated from sign-in",
  "monitor.check.update": "Data health check updated",
  "monitor.incident.acknowledge": "Incident assigned",
  "monitor.incident.dismiss": "Incident closed as not an issue",
  "audit.export": "Audit records exported",
  // ADR 0009 (round 8): Claude, ChatGPT and the like, as "Connected apps" calls them.
  "mcp.client_registered": "Connected app registered",
  "mcp.consent_approve": "Connected app approved",
  "mcp.consent_deny": "Connected app declined",
  "mcp.grant_issued": "Connected app signed in",
  "mcp.grant_revoked": "Connected app disconnected",
  "mcp.revoke_all": "Every connected app disconnected",
  "mcp.refresh_reuse": "Connected app token reused",
  "mcp.read": "Read through a connected app",
  "mcp.write_call": "Change asked for by a connected app",
  "mcp.write_approve": "Connected app change approved",
  "mcp.write_declined": "Connected app change declined",
  "mcp.switch_update": "Connected app access changed",
  "audit.review": "Audit report viewed",
  "audit.verify": "Audit trail verified",
  "audit.retention": "Archived audit month removed",
  "history.export": "History exported",
};

/**
 * An action family as the tenant trail's prefix filter offers it (round 7 T-36: `run.*` was a
 * machine name too). `customer` is never offered (the word Customer is not in the trail).
 */
export const AUDIT_ACTION_FAMILY_LABELS: Readonly<Record<string, string>> = {
  auth: "Every sign-in action",
  access: "Every access action",
  user: "Every user action",
  account: "Every email sign-in account action",
  group_mapping: "Every directory group action",
  customer: "Every tenant set-up action",
  tenant: "Every tenant action",
  canary: "Every canary action",
  instance: "Every instance action",
  binding: "Every destination settings action",
  connector: "Every connector action",
  dataflow: "Every connection action",
  hub: "Every source browse action",
  secret: "Every secret action",
  credential: "Every credential action",
  schedule: "Every schedule action",
  run: "Every run action",
  // History (ADR 0011): kept so stored Explorer rows still filter.
  explorer: "Every Explorer action",
  step: "Every run step action",
  dbt_job: "Every dbt job action",
  transformation: "Every dbt job action",
  settings: "Every settings action",
  migration: "Every migration action",
  notification: "Every notification action",
  monitor: "Every data health action",
  mcp: "Every connected app action",
  audit: "Every audit trail action",
  history: "Every history export action",
};

/** The entity a labelled row names in the grid, where it differs from the stored type. */
const AUDIT_ACTION_ENTITY_TYPES: Readonly<Record<string, string>> = {
  "customer.create": "tenant",
  "customer.update": "tenant",
  "transformation.create": "dbt_job",
  "transformation.update": "dbt_job",
  "transformation.delete": "dbt_job",
};

export function auditActionLabel(action: string): string | undefined {
  return Object.hasOwn(AUDIT_ACTION_LABELS, action)
    ? AUDIT_ACTION_LABELS[action as AuditAction]
    : undefined;
}

/** What a refused attempt reads as, where "created" would claim a change that never happened (#962). */
const FAILED_ACTION_LABELS: Readonly<Record<string, string>> = {
  "dataflow.create": "Connection create refused",
  "dataflow.update": "Connection update refused",
  "dataflow.delete": "Connection delete refused",
  // #1248: a destination save the server refused (an empty load field, a bad value).
  "binding.update": "Destination settings change refused",
};

/** #1248: a refused save of the dbt repository tab (`metadata.section: "transform_source"`). */
export const TRANSFORM_SOURCE_REFUSED_LABEL = "dbt repository change refused";

function isTransformSource(metadata: unknown): boolean {
  return (
    typeof metadata === "object" &&
    metadata !== null &&
    (metadata as { section?: unknown }).section === "transform_source"
  );
}

/** #1098: a `dataflow.revalidate` row whose test was not recorded (`metadata.recorded: false`). */
export const UNRECORDED_TEST_LABEL = "Connection tested, not recorded (nothing changed)";

function isUnrecordedTest(metadata: unknown): boolean {
  return (
    typeof metadata === "object" &&
    metadata !== null &&
    (metadata as { recorded?: unknown }).recorded === false
  );
}

/**
 * The words for a row: a failed attempt reads as refused, never as the change it tried to make.
 * An action without its own refused words reads "<words> (failed attempt)".
 */
export function auditActionLabelFor(
  action: string,
  failed: boolean,
  metadata?: unknown,
): string | undefined {
  const label = auditActionLabel(action);
  // #1098: a test of values the connection does not hold (or one a save overtook) changed
  // nothing on it, and says so.
  if (action === "dataflow.revalidate" && isUnrecordedTest(metadata)) {
    return UNRECORDED_TEST_LABEL;
  }
  if (!failed || label === undefined) return label;
  // #1248: the dbt repository tab saves through the same action; its row names its section.
  if (action === "binding.update" && isTransformSource(metadata)) {
    return TRANSFORM_SOURCE_REFUSED_LABEL;
  }
  return FAILED_ACTION_LABELS[action] ?? `${label} (failed attempt)`;
}

const HISTORICAL = new Set<string>(HISTORICAL_AUDIT_ACTIONS);

/**
 * An action as every trail's Action filter offers it (walk of 9a9d586, D-c: the tenant trail and
 * the runner's rows offered machine names where the grid shows words): the grid's words, with
 * "(history)" for an action no route writes any more, else the machine name the grid shows. The
 * value a filter sends is always the stored action.
 */
export function auditActionOptionText(action: string): string {
  const label = auditActionLabel(action);
  if (label === undefined) return action;
  return HISTORICAL.has(action) ? `${label} (history)` : label;
}

/**
 * An action family as a prefix filter offers it: its words ("Every run action"), with
 * "(history)" when every action of the family is historical (review r6fix2 N3:
 * `transformation.*` sat beside "dbt job created (history)" as though it were still written).
 * A family with no words (newer than this build) reads `family.*`.
 */
export function auditFamilyOptionText(family: string, actions: readonly string[]): string {
  const members = actions.filter((action) => action.startsWith(`${family}.`));
  const historical = members.length > 0 && members.every((action) => HISTORICAL.has(action));
  const words = Object.hasOwn(AUDIT_ACTION_FAMILY_LABELS, family)
    ? AUDIT_ACTION_FAMILY_LABELS[family]!
    : `${family}.*`;
  return historical ? `${words} (history)` : words;
}

/** The entity type the grid shows for a row: the stored one, except for a set-up row. */
export function auditEntityTypeLabel(action: string, entityType: string): string {
  return Object.hasOwn(AUDIT_ACTION_ENTITY_TYPES, action)
    ? AUDIT_ACTION_ENTITY_TYPES[action]!
    : entityType;
}

/**
 * A read event records that something was looked at; it changed nothing (round 6 walk W20:
 * `dataflow.read` and `binding.read` offered "View change"). Its row opens the drawer as
 * "View details", and the drawer lists what was read, never a before and after (#1376).
 */
export function isReadEvent(action: string): boolean {
  return action.endsWith(".read");
}

/**
 * What kind of read a `*.read` row records, so the drawer says only what happened (#1373 review).
 * `record`: a person opened a record and the row stores what it showed (`dataflow.read`,
 * `binding.read`). `secret`: a stored secret was used for the person's request (`secret.read`,
 * a Connect probe or a binding save's copy), and the row names only where it is stored, never
 * the value. `connected_app`: an AI app called a read tool on the person's behalf (`mcp.read`).
 * `other`: any other read (the dbt source reads), which stores no record. Null for a non-read.
 */
export type AuditReadKind = "record" | "secret" | "connected_app" | "other";

const RECORD_READS: ReadonlySet<string> = new Set(["dataflow.read", "binding.read"]);

export function auditReadKind(action: string): AuditReadKind | null {
  if (!isReadEvent(action)) return null;
  if (RECORD_READS.has(action)) return "record";
  if (action === "secret.read") return "secret";
  if (action === "mcp.read") return "connected_app";
  return "other";
}
